interface TokenStorageStrategy {
  setItem(key: string, value: string): void;
  getItem(key: string): string | null;
  removeItem(key: string): void;
  clear(): void;
}

class LocalStorageStrategy implements TokenStorageStrategy {
  setItem(key: string, value: string): void {
    if (typeof window !== 'undefined') {
      localStorage.setItem(key, value);
    }
  }

  getItem(key: string): string | null {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(key);
    }
    return null;
  }

  removeItem(key: string): void {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(key);
    }
  }

  clear(): void {
    if (typeof window !== 'undefined') {
      localStorage.clear();
    }
  }
}

class SessionStorageStrategy implements TokenStorageStrategy {
  setItem(key: string, value: string): void {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(key, value);
    }
  }

  getItem(key: string): string | null {
    if (typeof window !== 'undefined') {
      return sessionStorage.getItem(key);
    }
    return null;
  }

  removeItem(key: string): void {
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem(key);
    }
  }

  clear(): void {
    if (typeof window !== 'undefined') {
      sessionStorage.clear();
    }
  }
}

class MemoryStorageStrategy implements TokenStorageStrategy {
  private storage: Map<string, string> = new Map();

  setItem(key: string, value: string): void {
    this.storage.set(key, value);
  }

  getItem(key: string): string | null {
    return this.storage.get(key) || null;
  }

  removeItem(key: string): void {
    this.storage.delete(key);
  }

  clear(): void {
    this.storage.clear();
  }
}

export class TokenStorage {
  private strategy: TokenStorageStrategy;
  private readonly ACCESS_TOKEN_KEY = '@lookme::accessToken';
  private readonly REFRESH_TOKEN_KEY = '@lookme::refreshToken';

  constructor(strategy: 'localStorage' | 'sessionStorage' | 'memory' = 'localStorage') {
    switch (strategy) {
      case 'sessionStorage':
        this.strategy = new SessionStorageStrategy();
        break;
      case 'memory':
        this.strategy = new MemoryStorageStrategy();
        break;
      case 'localStorage':
      default:
        this.strategy = new LocalStorageStrategy();
        break;
    }
  }

  setAccessToken(token: string): void {
    this.strategy.setItem(this.ACCESS_TOKEN_KEY, token);
  }

  getAccessToken(): string | null {
    return this.strategy.getItem(this.ACCESS_TOKEN_KEY);
  }

  removeAccessToken(): void {
    this.strategy.removeItem(this.ACCESS_TOKEN_KEY);
  }

  setRefreshToken(token: string): void {
    this.strategy.setItem(this.REFRESH_TOKEN_KEY, token);
  }

  getRefreshToken(): string | null {
    return this.strategy.getItem(this.REFRESH_TOKEN_KEY);
  }

  removeRefreshToken(): void {
    this.strategy.removeItem(this.REFRESH_TOKEN_KEY);
  }

  clearTokens(): void {
    this.removeAccessToken();
    this.removeRefreshToken();
  }

  setStrategy(strategy: 'localStorage' | 'sessionStorage' | 'memory'): void {
    const accessToken = this.getAccessToken();
    const refreshToken = this.getRefreshToken();

    switch (strategy) {
      case 'sessionStorage':
        this.strategy = new SessionStorageStrategy();
        break;
      case 'memory':
        this.strategy = new MemoryStorageStrategy();
        break;
      case 'localStorage':
      default:
        this.strategy = new LocalStorageStrategy();
        break;
    }

    if (accessToken) this.setAccessToken(accessToken);
    if (refreshToken) this.setRefreshToken(refreshToken);
  }
}

export default new TokenStorage('localStorage');